/**
 * Alert Column Component
 * Vertical scrolling feed for a specific alert category (aggr.trade style)
 */

import { useMemo, useRef, useEffect, useState, useCallback, memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAlertStore, type Alert, type Trade } from '@/stores/alert-store';
import { formatRelativeTime, formatUSD, formatPrice, cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface ColumnConfig {
  readonly id: string;
  readonly title: string;
  readonly types: readonly string[];
  readonly includeTrades?: boolean;
}

interface FeedItem {
  readonly id: string;
  readonly timestamp: number;
  readonly asset: string;
  readonly isBullish: boolean;
  readonly isBearish: boolean;
  readonly label: string;
  readonly message: string;
  readonly usd: number | null;
  readonly exchange: string | null;
  readonly price: number | null;
  readonly size: number | null;
}

// ============================================================================
// TRANSFORMS
// ============================================================================

function alertToFeedItem(alert: Alert): FeedItem {
  const direction = alert.direction
    || (String(alert.type).includes('LONG') ? 'LONG' : undefined)
    || (String(alert.type).includes('SHORT') ? 'SHORT' : undefined);

  return {
    id: alert.id,
    timestamp: alert.timestamp,
    asset: alert.asset,
    isBullish: direction === 'LONG' || String(alert.type).includes('BUY'),
    isBearish: direction === 'SHORT' || String(alert.type).includes('SELL'),
    label: formatAlertType(alert.type),
    message: typeof alert.message === 'string' ? alert.message : '',
    usd: typeof alert.usd === 'number' ? alert.usd : null,
    exchange: typeof alert.exchange === 'string' ? alert.exchange : null,
    price: typeof alert.price === 'number' ? alert.price : null,
    size: null,
  };
}

function tradeToFeedItem(trade: Trade): FeedItem {
  return {
    id: trade.id,
    timestamp: trade.timestamp,
    asset: trade.asset,
    isBullish: trade.side === 'buy',
    isBearish: trade.side === 'sell',
    label: trade.exchange,
    message: '',
    usd: trade.usd,
    exchange: trade.exchange,
    price: trade.price,
    size: trade.size,
  };
}

function formatAlertType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============================================================================
// COMPACT ROW
// ============================================================================

interface CompactRowProps {
  readonly item: FeedItem;
}

const CompactRow = memo(function CompactRow({ item }: CompactRowProps) {
  const borderColor = item.isBullish
    ? 'border-l-green-500'
    : item.isBearish
      ? 'border-l-red-500'
      : 'border-l-muted-foreground/30';

  return (
    <div
      className={cn(
        'border-l-2 px-2 py-1 text-xs transition-colors hover:bg-accent/30',
        borderColor
      )}
    >
      {/* Row 1: asset + label + time */}
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'font-bold',
            item.isBullish && 'text-green-500',
            item.isBearish && 'text-red-500',
            !item.isBullish && !item.isBearish && 'text-foreground'
          )}
        >
          {item.asset}
        </span>
        <span className="truncate text-muted-foreground">{item.label}</span>
        <span className="ml-auto shrink-0 text-muted-foreground/70">
          {formatRelativeTime(item.timestamp)}
        </span>
      </div>

      {/* Row 2: price / trade details / message */}
      {(item.message || item.usd !== null || item.price !== null) && (
        <div className="mt-0.5 flex items-center gap-1.5">
          {item.price !== null && (
            <span className="font-mono font-medium text-foreground">
              {formatPrice(item.price)}
            </span>
          )}
          {item.usd !== null && (
            <span
              className={cn(
                'font-mono font-semibold',
                item.isBullish && 'text-green-500',
                item.isBearish && 'text-red-500'
              )}
            >
              {formatUSD(item.usd)}
            </span>
          )}
          {item.size !== null && (
            <span className="text-muted-foreground">
              {item.size.toFixed(4)}
            </span>
          )}
          {item.message && !item.usd && item.price === null && (
            <span className="truncate text-foreground/80">{item.message}</span>
          )}
          {item.message && (item.usd !== null || item.price !== null) && (
            <span className="truncate text-muted-foreground">{item.message}</span>
          )}
        </div>
      )}
    </div>
  );
});

// ============================================================================
// COLUMN COMPONENT
// ============================================================================

interface AlertColumnProps {
  readonly config: ColumnConfig;
}

export function AlertColumn({ config }: AlertColumnProps) {
  const alerts = useAlertStore((s) => s.alerts);
  const trades = useAlertStore((s) => s.trades);
  const selectedAsset = useAlertStore((s) => s.selectedAsset);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const prevCountRef = useRef(0);

  // Build feed items from alerts + trades
  const items = useMemo(() => {
    // Case-insensitive type matching (backend sends UPPERCASE, config may use lowercase)
    const typeSet = new Set(config.types.map((t) => t.toUpperCase()));

    const alertItems: FeedItem[] = alerts
      .filter((a) => typeSet.has(a.type.toUpperCase()) && a.asset === selectedAsset)
      .map(alertToFeedItem);

    const tradeItems: FeedItem[] = config.includeTrades
      ? trades
          .filter((t) => t.asset === selectedAsset)
          .map(tradeToFeedItem)
      : [];

    return [...alertItems, ...tradeItems]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 200);
  }, [alerts, trades, selectedAsset, config.types, config.includeTrades]);

  // Virtualizer — only renders visible rows + overscan
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  // Auto-scroll to top when new items arrive
  useEffect(() => {
    if (autoScroll && items.length > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    prevCountRef.current = items.length;
  }, [items.length, autoScroll]);

  // Detect user scroll to pause auto-scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // If user scrolled down from top, pause auto-scroll
    setAutoScroll(el.scrollTop < 10);
  }, []);

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="flex h-full flex-col overflow-hidden bg-card">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border px-2 py-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {config.title}
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground/60">
          {items.length}
        </span>
      </div>

      {/* Feed */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        {items.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4 text-xs text-muted-foreground">
            No {config.title.toLowerCase()} yet
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualItems.map((virtualRow) => {
              const item = items[virtualRow.index]!;
              return (
                <div
                  key={item.id}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  className="absolute left-0 top-0 w-full border-b border-border/50"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <CompactRow item={item} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && items.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setAutoScroll(true);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = 0;
            }
          }}
          className="shrink-0 border-t border-border bg-accent/50 px-2 py-1 text-center text-[10px] text-muted-foreground hover:bg-accent"
        >
          Scroll paused — click to resume
        </button>
      )}
    </div>
  );
}
